import { useBoard } from '@/features/project-board/BoardProvider';
import type { BoardSource } from '@/features/project-board/types';
import { KstDateTime } from '@/shared/datetime';

/**
 * 하네스 저장소 선택 — `전체 / market / uncle`.
 *
 * 다른 필터와 나란히 두지 않고 **한 단 위에 세그먼트로** 둔다. 이건 "무엇을 좁혀 볼까"가 아니라
 * "어느 저장소를 보고 있나"라서, 지금 보고 있는 범위가 늘 보여야 숫자를 잘못 읽지 않는다.
 *
 * 목록은 색인 실행 기록에서 온다 — 저장소를 추가하면 색인 한 번 뒤에 여기 자동으로 나타난다.
 */
export function SourceTabs(): React.JSX.Element | null {
  const { sources, filters, setFilters } = useBoard();

  // 저장소가 하나뿐이면 고를 게 없다 — 늘 켜진 탭 하나는 화면만 차지한다.
  if (sources.length < 2) return null;

  const options = [{ key: '', label: '전체' }, ...sources.map((source) => ({ key: source.key, label: source.key }))];

  return (
    <div className="mb-3 flex items-center gap-2">
      <div
        className="inline-flex overflow-hidden rounded-md border border-line bg-surface"
        role="group"
        aria-label="하네스 저장소"
      >
        {options.map((option) => (
          <button
            key={option.key}
            type="button"
            aria-pressed={option.key === filters.source}
            // 에픽·담당자는 저장소 안에서만 뜻이 있다 — 바꾸면서 들고 가면 결과가 0건이 된다.
            onClick={() => setFilters({ source: option.key, epic: '', owner: '' })}
            className={`px-3 py-1.5 text-[12.5px] not-first:border-l not-first:border-line ${
              option.key === filters.source ? 'bg-accent-soft font-semibold text-accent' : 'text-muted hover:bg-line-soft'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
      <span className="text-[11px] text-faint">{scanHint(sources, filters.source)}</span>
    </div>
  );
}

/** 색인 시점 — 저장소를 고르면 그 저장소 것, 전체면 가장 오래된 것(화면에서 가장 낡은 값이 기준이다). */
function scanHint(sources: readonly BoardSource[], selected: string): string {
  const scoped = selected === '' ? sources : sources.filter((source) => source.key === selected);
  const oldest = scoped
    .map((source) => source.scannedAt)
    .sort()
    .at(0);
  return oldest ? `색인 ${KstDateTime.format(oldest)}` : '';
}
